import { makeAutoObservable } from 'mobx';
import agent from '../services/api/agent';
import { store } from './store';
import { timeout } from '../utils/timeout';
import { debounce } from '../utils/Debounce';
import { IPropertyGroup, IPropertyGroupColor, IPropertyValue, IPropertyValueObject, TPropertyValueView } from '../services/models/PropertyViewModel';
import { createDefaultGoogleDraft, GOOGLE_DEFAULT_LANGUAGE, GOOGLE_TTS_MODEL, GoogleAiVoiceDraftV1, isGoogleAiVoiceDraft, isGoogleTtsModel } from '../components/aiVoice/google/types';
import { gunzipToUtf8, readTtsMetadataFromMp3Url } from '../utils/mp3Metadata';

interface ISaveTimer {
  timeLastChange: number;
  timeUpdateServer: number;
  timeLastSave: number;
  timeForceSave: number;
}

type PagePropertiesWithSound = {
  courseID?: number;
  coursePageID?: number;
  sound?: {
    items?: {
      ttsPrompt?: TPropertyValueView;
      URL?: TPropertyValueView;
      url?: TPropertyValueView;
    };
  };
};

const createFallbackGoogleDraftWithoutRememberedLanguage = (): GoogleAiVoiceDraftV1 => {
  const fallback = createDefaultGoogleDraft();
  return {
    ...fallback,
    languageCode: GOOGLE_DEFAULT_LANGUAGE,
  };
};

export default class PropertyStore {
  constructor() {
    makeAutoObservable(this);
  }

  saveTimeout: NodeJS.Timeout | undefined = undefined;
  propertyTimer: ISaveTimer = { timeLastChange: 0, timeLastSave: 0, timeUpdateServer: 500, timeForceSave: 20000 };

  private isMatchingPage = (
    pageProperties: PagePropertiesWithSound | undefined,
    courseID: string | number,
    pageID: string | number,
  ): pageProperties is PagePropertiesWithSound => {
    if (!pageProperties) return false;
    return String(pageProperties.courseID) === String(courseID) && String(pageProperties.coursePageID) === String(pageID);
  };

  private forEachMatchingLoadedPagePropertiesByIds = (
    courseID: string | number,
    pageID: string | number,
    callback: (pageProperties: PagePropertiesWithSound) => void,
  ) => {
    const loadedPageProperties: Array<PagePropertiesWithSound | undefined> = [store.pageStore.coursePageProperties, store.courseStore.pageProperties];
    for (const pageProperties of loadedPageProperties) {
      if (!this.isMatchingPage(pageProperties, courseID, pageID)) continue;
      callback(pageProperties);
    }
  };

  private getSoundAudioUrl = (pageProperties: PagePropertiesWithSound | undefined): string => {
    const upperCaseUrl = pageProperties?.sound?.items?.URL?.option?.value;
    if (typeof upperCaseUrl === 'string' && upperCaseUrl.trim().length > 0) return upperCaseUrl;

    // Legacy compatibility while migrating to canonical `URL`.
    const lowerCaseUrl = pageProperties?.sound?.items?.url?.option?.value;
    if (typeof lowerCaseUrl === 'string' && lowerCaseUrl.trim().length > 0) return lowerCaseUrl;

    return '';
  };

  private updateLoadedTtsPromptValuesByIds = (courseID: string | number, pageID: string | number, value: string) => {
    this.forEachMatchingLoadedPagePropertiesByIds(courseID, pageID, (pageProperties) => {
      const property = pageProperties.sound?.items?.ttsPrompt;
      if (property) property.option.value = value;
    });
  };

  private getPagePropertiesByIds = async (courseID: string | number, pageID: string | number) => {
    let loadedProperties: PagePropertiesWithSound | undefined;
    this.forEachMatchingLoadedPagePropertiesByIds(courseID, pageID, (pageProperties) => {
      loadedProperties = pageProperties;
    });

    if (loadedProperties) return loadedProperties;

    const parsedCourseID = Number(courseID);
    const parsedPageID = Number(pageID);
    if (!Number.isFinite(parsedCourseID) || !Number.isFinite(parsedPageID)) return undefined;

    try {
      return await agent.Page.getProperties(parsedCourseID, parsedPageID);
    } catch {
      return undefined;
    }
  };

  private getInvalidPersistedDraftResult = (): { draft: GoogleAiVoiceDraftV1; exists: true } => ({
    draft: createFallbackGoogleDraftWithoutRememberedLanguage(),
    exists: true,
  });

  private normalizeGoogleDraft = (draft: GoogleAiVoiceDraftV1): GoogleAiVoiceDraftV1 => ({
    ...draft,
    model: isGoogleTtsModel(draft.model) ? draft.model : GOOGLE_TTS_MODEL,
    languageCode: draft.languageCode || GOOGLE_DEFAULT_LANGUAGE,
  });

  private parseGoogleDraft = (value: string): GoogleAiVoiceDraftV1 | undefined => {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (!isGoogleAiVoiceDraft(parsed)) return undefined;
      return this.normalizeGoogleDraft(parsed);
    } catch {
      return undefined;
    }
  };

  setProperty = (propertyValue: TPropertyValueView, value: string) => {
    this.setOption(propertyValue, value);
  };

  setPropertyByValueID = (propertyValueID: number, value: string) => {
    this.setOptionByValueID(propertyValueID, value);
  };

  setDesignProperty = (propertyValue: TPropertyValueView, value: string) => {
    this.setOption(propertyValue, value);
    timeout(500).then(() => store.pageStore.savePageHTMLTimer()); //This delay is so the placeholder actually registers the change before we ask for the HTML in SavePAgeHTMLTimer function.
  };

  setDesignPropertyList = (propertyGroup: IPropertyGroupColor | IPropertyGroup, optionList: IPropertyValue[]) => {
    return new Promise(() => {
      this.setOptionListDebounced(propertyGroup, optionList); // This delay is so the placeholder actually registers the change before we ask for the HTML in SavePageHTMLTimer function.
    });
  };

  setOptionList = (propertyGroup: IPropertyGroupColor | IPropertyGroup, optionList: IPropertyValue[]) => {
    return new Promise(() => {
      const options: IPropertyValueObject[] = [];

      optionList.forEach((option) => {
        options.push({
          value: option.value,
          propertyValueID: option.propertyValueView.propertyValueID !== 0 ? option.propertyValueView.propertyValueID : undefined,
          valueMapID: option.propertyValueView.propertyValueID !== 0 ? undefined : option.propertyValueView.valueMapID,
        });
      });

      agent.Property.setOptions(options)
        .then((res) => {
          res.forEach((opt) => {
            const keys = Object.keys(propertyGroup.items);
            if (!opt.valueMapID) return;
            const value = keys.find((x) => x === opt.valueMapID?.propertyReactName);
            if (!value || !opt.propertyValueID) return;
            propertyGroup.items[value].propertyValueID = opt.propertyValueID;
          });
        })
        .then(() => {
          timeout(500).then(() => store.pageStore.savePageHTMLTimer());
        });
    });
  };

  setOptionListDebounced = debounce(this.setOptionList, 500);

  setOption = async (aPropertyValueView: TPropertyValueView, value: string) => {
    return agent.Property.setOption(
      value,
      aPropertyValueView.propertyValueID !== 0 ? aPropertyValueView.propertyValueID : undefined,
      aPropertyValueView.propertyValueID !== 0 ? undefined : aPropertyValueView.valueMapID,
    ).then((res) => {
      if (res.propertyValueID) aPropertyValueView.propertyValueID = res.propertyValueID;
      return res;
    });
  };

  setOptionByValueID = async (propertyValueID: number, value: string) => {
    agent.Property.setOption(value, propertyValueID, undefined);
  };

  saveGoogleAIVoiceDraftByIds = async (courseID: string | number, pageID: string | number, draft: GoogleAiVoiceDraftV1): Promise<boolean> => {
    const pageProperties = await this.getPagePropertiesByIds(courseID, pageID);
    const ttsPromptProperty = pageProperties?.sound?.items?.ttsPrompt;
    if (!ttsPromptProperty) return false;

    const draftJson = JSON.stringify(draft);
    await this.setOption(ttsPromptProperty, draftJson);
    ttsPromptProperty.option.value = draftJson;
    this.updateLoadedTtsPromptValuesByIds(courseID, pageID, draftJson);
    return true;
  };

  saveGoogleAIVoiceDraft = async (draft: GoogleAiVoiceDraftV1): Promise<boolean> => {
    const courseID = store.pageStore.coursePage?.courseID ?? store.courseStore.pageProperties?.courseID;
    const pageID = store.pageStore.coursePage?.coursePageID ?? store.courseStore.pageProperties?.coursePageID;
    if (courseID === undefined || pageID === undefined) return false;
    return this.saveGoogleAIVoiceDraftByIds(courseID, pageID, draft);
  };

  getGoogleAIVoiceDraftByIdsWithMeta = async (
    courseID: string | number,
    pageID: string | number,
  ): Promise<{ draft: GoogleAiVoiceDraftV1; exists: boolean }> => {
    const pageProperties = await this.getPagePropertiesByIds(courseID, pageID);
    const ttsPromptProperty = pageProperties?.sound?.items?.ttsPrompt;
    const ttsPromptValue = ttsPromptProperty?.option?.value ?? '';

    if (ttsPromptValue.trim().length > 0) {
      const parsedFromTtsPrompt = this.parseGoogleDraft(ttsPromptValue);
      if (parsedFromTtsPrompt) return { draft: parsedFromTtsPrompt, exists: true };

      return this.getInvalidPersistedDraftResult();
    }

    const audioUrl = this.getSoundAudioUrl(pageProperties);
    if (!audioUrl) {
      return { draft: createDefaultGoogleDraft(), exists: false };
    }

    try {
      const metadata = await readTtsMetadataFromMp3Url(audioUrl);
      if (!metadata.canonicalGzipPayload) {
        return { draft: createDefaultGoogleDraft(), exists: false };
      }

      try {
        const canonicalPayload = await gunzipToUtf8(metadata.canonicalGzipPayload);
        const parsedFromMetadata = this.parseGoogleDraft(canonicalPayload);
        if (!parsedFromMetadata) {
          return this.getInvalidPersistedDraftResult();
        }

        return { draft: parsedFromMetadata, exists: false };
      } catch {
        return this.getInvalidPersistedDraftResult();
      }
    } catch {
      return { draft: createDefaultGoogleDraft(), exists: false };
    }
  };

  getGoogleAIVoiceDraftByIds = async (courseID: string | number, pageID: string | number): Promise<GoogleAiVoiceDraftV1> => {
    const { draft } = await this.getGoogleAIVoiceDraftByIdsWithMeta(courseID, pageID);
    return draft;
  };

  getGoogleAIVoiceDraftWithMeta = async (): Promise<{ draft: GoogleAiVoiceDraftV1; exists: boolean }> => {
    const courseID = store.pageStore.coursePage?.courseID ?? store.courseStore.pageProperties?.courseID;
    const pageID = store.pageStore.coursePage?.coursePageID ?? store.courseStore.pageProperties?.coursePageID;
    if (courseID === undefined || pageID === undefined) {
      return { draft: createDefaultGoogleDraft(), exists: false };
    }
    return this.getGoogleAIVoiceDraftByIdsWithMeta(courseID, pageID);
  };

  getGoogleAIVoiceDraft = async (): Promise<GoogleAiVoiceDraftV1> => {
    const courseID = store.pageStore.coursePage?.courseID ?? store.courseStore.pageProperties?.courseID;
    const pageID = store.pageStore.coursePage?.coursePageID ?? store.courseStore.pageProperties?.coursePageID;
    if (courseID === undefined || pageID === undefined) return createDefaultGoogleDraft();
    return this.getGoogleAIVoiceDraftByIds(courseID, pageID);
  };
}
